/* eslint-disable max-classes-per-file */
import H5Pay from '@hb/h5-pay';
import { PayOptions } from '@hb/h5-pay/dist/type';
import * as hbridge from '@hb/mbridge';
import { compareVersions } from 'compare-versions';
import { Toast } from 'vant';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import { uiMgr } from '../../common/uiMgr';
import { getAppEnv, getSystemCode, isBoComTinyApp } from '../../common/env/env';
import nativeMgr, { CityInfo } from '../../common/native/native';
import {
  appQueryConfig,
  getLastQueryVal,
  parseQueryStr,
} from '../../common/utils/url';
import { setParamsToUrl } from '../../common/utils/params';
import logger from '../../common/utils/logger';
import { dispatchPayInfo } from '../../controller/dispatchs/commonDispatchs';
import { RouterTicketPayResult } from '../../constants/routerConstants';
import { handlePostOrderRefresh } from './reward';
import { CouponPromoTypeEnum, ApiEnums } from '../../constants/apiConstants';
import store from '../store';
import {
  PayInfo, PayResult, SkuDetailData, SignPayInfoType,
} from "../store/modules/ticket";
import type { TicketTabData } from "../store/modules/ticket";
import { BuyMultiGoodsRes, reqBuyMultiGoods } from './ticketApis';
import { COUPON_MONTHLY_SCENE, ORDER_TYPE, PASSWORD_SCENE } from '../../common/constants/commonConstants';
import { httpMgr } from '../../common/network/httpMgr';

const { env } = hbridge;

// ==================== 类型定义和枚举 ====================

/** 支付类型枚举 */
export enum PaymentType {
  ALIPAY_MONTHLY = 'ALIPAY_MONTHLY' /** 支付宝连续包月 */,
  DOUYIN_PASSWORD_FREE = 'DOUYIN_PASSWORD_FREE' /** 抖音免密签约 */,
  DOUYIN_MONTHLY = 'DOUYIN_MONTHLY' /** 抖音连续包月（原有） */,
  NORMAL = 'NORMAL' /** 普通商品 */,
}

/** 商品支付信息接口 */
interface SkuPaymentInfo {
  monthlyGoods?: boolean /** 是否为连续包月商品 */;
  monthlyContractChannel?: string /** 包月签约渠道 */;
  passwordFreeGoods?: boolean /** 是否为免密签约商品 */;
  passwordFreeContractChannel?: string /** 免密签约渠道 */;
}

/** 支付类型配置常量 */
const CHARGE_TYPE_CONFIG = {
  ALIPAY_MONTHLY: 1309 /** 支付宝连续包月 */,
  DOUYIN_PASSWORD_FREE: 1307 /** 抖音免密签约 */,
  DOUYIN_MONTHLY: 1308 /** 抖音连续包月（原有） */,
} as const;

/** 支付渠道配置常量 */
const PAYMENT_CHANNEL_CONFIG = {
  ALIPAY_MONTHLY: {
    channel: '102' /** 支付宝渠道 */,
    signChannel: 'alipay' /** 支付宝签约渠道 */,
    channelOperationInfoExt: undefined /** 无扩展参数 */,
  },
  DOUYIN_PASSWORD_FREE: {
    channel: '2304' /** 抖音免密签约渠道 */,
    signChannel: '' /** 签约渠道为空 */,
    channelOperationInfoExt: undefined /** 无扩展参数 */,
  },
  DOUYIN_MONTHLY: {
    channel: '2309' /** 抖音连续包月渠道 */,
    signChannel: '' /** 签约渠道为空 */,
    channelOperationInfoExt: '{"productTagForDySign":"hlbikebaoyue1","biz_scene":"hello1centcard"}' /** 抖音扩展参数 */,
  },
} as const;

// ==================== 微信小程序配置 ====================

/**
 * 所有卡券商城适配过的微信小程序配置
 *
 * 没有指定payPath的都会走h5-sdk的默认支付路径
 */
const WechatAppConfig: Record<string, {
  systemCode?: string;
  payPath?: string;
}> = {
  /** 电动车小程序 */
  wxa13c283ec22c0c22: {},
  /** 微信主小程序 */
  wxb937e3d0b3ca117e: {},
  /** 送货小程序 */
  wx3ee44f85904a708c: {},
  /** 租车小程序 */
  wx6ee6550b839c3a87: {},
  /** 微信充电小程序 */
  wx5204c8e0c93867b1: {
    payPath: '/pages/Pay/Pay',
    systemCode: 'Ar4',
  },
  /** 微信加油小程序 */
  wx4c58739e1deffa50: {},
  /** 打车顺风车/普惠小程序 */
  wxda591e289442a50d: {
    systemCode: 'C4',
    payPath: '/pages/minipro-pay/index',
  },
  /** 顺风车独立小程序 */
  wx5b8f2b678eb20a9b: {
    systemCode: 'C8',
    payPath: '/pages/minipro-pay/index',
  },
};
/**
 * 所有卡券商城适配过的抖音小程序配置
 *
 * 没有指定payPath的都会走h5-sdk的默认支付路径
 */
const TTAppConfig: Record<string, {
  systemCode?: string;
  payPath?: string;
}> = {
  /** 抖音主小程序 */
  tt58fd2e40c7c94a3701: {
    payPath: '/pages/pay/index',
  },

};

/**
   * result 为字符串1或者0
   * billNo 为订单号
   * isPushPage 页面是否是push方式打开
   */
export const gotoPayResult = ({
  payInfo,
  isPushPage,
}: {
  payInfo: PayResult;
  isPushPage?: boolean;
}) => {
  // 将数据丢到store里
  dispatchPayInfo(payInfo);

  // 跳转页面
  if (isPushPage) {
    uiMgr.pushPage({
      name: RouterTicketPayResult.name,
      query: {
        isSuccess: payInfo.result,
        categoryId: `${payInfo.categoryId}`,
      },
    });
  } else {
    uiMgr.redirectPage({
      name: RouterTicketPayResult.name,
      query: {
        isSuccess: payInfo.result,
        categoryId: `${payInfo.categoryId}`,
      },
    });
  }
};

export const pay = async (
  info: PayInfo,
  { isPushPage = false, successCallback }: {
    isPushPage?: boolean,
    successCallback?: () => void,
  } = {},
) => {
  const { payOrderInfo, ...restPayInfo } = info;
  const u = navigator.userAgent;
  const isBoc = u.indexOf('bocapp(') > -1;
  const multiOrders = Array.isArray(payOrderInfo);
  const resolvedOrders = multiOrders ? payOrderInfo : [payOrderInfo];
  const goResult = (result = '1') => {
    gotoPayResult({
      payInfo: {
        result,
        ...restPayInfo,
        payOrderInfo,
      },
      isPushPage,
    });
  };

  try {
    if (!multiOrders) {
      const payMoney = payOrderInfo ? +payOrderInfo.payMoney : 0;
      if (!(payMoney > 0)) {
        // 如果支付金额小与等于0，则直接成功
        goResult();
        return;
      }
    }

    let adCode = '';
    let cityCode = '';
    let appCityInfo: CityInfo = null;
    try {
      appCityInfo = await nativeMgr.getCityInfo();
      adCode = appCityInfo?.adCode || '';
      cityCode = appCityInfo?.cityCode || '';
      // alert(JSON.stringify(appCityInfo));
    } catch (error) {
      // noop
    }

    const {
      appId,
      ...restAppQueryConfig
    } = appQueryConfig;
    /** 微信小程序在卡券商城中的配置 */
    const wechatConfig = env.isWechatMini
      ? WechatAppConfig[appId]
      : null;
    const isTT = (env as any).isTTMP;
    const ttConfig = isTT ? TTAppConfig[appId] : null;
    logger('开始支付了~~~');
    logger(`appId: ${appId}`);
    const hasSystemCode = wechatConfig?.systemCode || ttConfig?.systemCode || '';
    logger(hasSystemCode, 'hasSystemCode');
    const h5Pay = new H5Pay({
      env: getAppEnv(),
      systemCode: hasSystemCode || getSystemCode(),
      token: window.$$user.token || '',
      ubt: UBT,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // currentSdkVersion: "2.4.0",
      hbridge,
    });
    const payList = resolvedOrders.map(({
      payOrderId,
      payMoney,
      businessType,
      billNo,
    }) => ({
      guid: payOrderId,
      amount: payMoney,
      businessType,
      orderId: billNo,
    }));

    let returnUrl = `${window.location.origin}${window.location.pathname}${
      window.location.search
    }#/order${multiOrders ? '' : `/${payOrderInfo.billNo}`}?appId=${appId}`;
    logger(appId, 'appId~~~~~~');

    if (wechatConfig || ttConfig) {
      // 如果是哈啰相关的小程序，则跳转到支付成功页面，先将支付数据缓存
      sessionStorage.setItem(
        'pay_info',
        JSON.stringify({
          result: '1',
          ...info,
        }),
      );
      returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`
        + `#/TicketPayResult?isSuccess=1&appId=${appId}&categoryId=${info.categoryId}`;
    }

    returnUrl = setParamsToUrl(restAppQueryConfig, returnUrl);
    const query = parseQueryStr();
    const mpVersion = getLastQueryVal(query.mpVersion);
    logger('returnUrl', returnUrl);
    logger(payOrderInfo, 'payOrderInfo');
    logger('old mpVersion', mpVersion);

    if (env.isWechatMini && mpVersion) {
      const strArr = mpVersion.split('#');
      const newMp = strArr.length ? strArr[0] : '';
      logger('new mpVersion', newMp);
      const result = compareVersions(newMp, '6.23.0');
      if (result < 0) return;
    }

    /** 小程序环境额外的h5-sdk支付配置 */
    const wxMini: NonNullable<PayOptions['extra']>['wxMini'] = {};
    const ttMini: NonNullable<PayOptions['extra']>['wxMini'] = {};
    if (wechatConfig?.payPath) {
      wxMini.miniPayPath = wechatConfig.payPath;
    }
    if (ttConfig?.payPath) {
      ttMini.miniPayPath = ttConfig.payPath;
    }

    const [firstOrder] = resolvedOrders;

    // 显示支付加载中提示
    if (isBoComTinyApp) { uiMgr.showLoading(); }

    await h5Pay.pay({
      payParams: {
        businessType: firstOrder.businessType,
        businessScene: firstOrder.businessScene,
        payMoney: firstOrder.payMoney,
        adCode,
        cityCode,
        payList,
        sourceType: 9,
      },
      /**
       * 哈啰出行app，哈啰出行支付宝小程序不需token
       * 其他环境则需要token，不传token，则走不登陆支付方式
       */
      token: window.$$user.token,
      returnUrl,
      /**
       * 支付成功之后的跳转页面
       */
      successPageUrl: '',
      /**
       * 支付失败之后的跳转页面
       */
      failPageUrl: '',
      extra: {
        wxMini,
        ttMini,
      },
    });

    // 支付成功，隐藏 loading
    if (isBoComTinyApp) { uiMgr.hideLoading(); }

    if (appQueryConfig.activityId === 'MALL_ACT_23930') {
      const fullHash = window.location.hash; // 获取完整的hash，例如 "#section1?name=test&age=20"
      const hashValue = fullHash.split('?')[0].substring(1);
      // 购买记录页支付完成刷新一下整个页面
      if (hashValue === '/order') {
        window.location.reload();
      } else {
        window.history.back();
      }
      return;
    }
    if (isBoc) {
      Toast.success('购买成功');
      window.history.go(-1);
    } else {
      if (typeof successCallback === 'function') {
        // 延迟执行，尽量避免支付成功后服务端数据还未同步
        setTimeout(() => {
          successCallback();
        }, 500);
      }
      goResult();
    }
  } catch (err) {
    // 支付失败或取消，隐藏 loading
    if (isBoComTinyApp) { uiMgr.hideLoading(); }

    // console.log('ggg', err);
    type PayErr = { code: string } | null;

    if ((err as PayErr)?.code === '2002') {
      // * 不处理取消支付
      // noop
    } else if (isBoc) {
      Toast.fail('支付失败，请稍后重试');
    } else {
      logger('[bikeMall] 🏦 支付失败，调用goResult');
      goResult('0');
    }

    throw err; // 处理完之后将错误重新抛出去
  }
};

/** h5pay实例 */
let h5PayInst: H5Pay;

/** 获取h5pay实例 */
export const getH5PayInst = () => {
  if (!h5PayInst) {
    h5PayInst = new H5Pay({
      env: getAppEnv(),
      systemCode: getSystemCode(),
      token: window.$$user.token,
      ubt: UBT,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      hbridge,
    });
  }

  return h5PayInst;
};

export class H5PayError extends Error {
  code: string;

  constructor(msg: string, code: string) {
    super(msg);
    this.code = code;
  }
}

/** 请求支付订单 */
export const reqOrderPay = async (
  data: BuyMultiGoodsRes & {
    returnUrl: string;
  },
) => {
  const {
    payOrderId,
    orderId,
    businessType,
    businessScene,
    payMoney,
    returnUrl,
  } = data;
  const h5Pay = getH5PayInst();
  // * 对h5Pay.pay的错误处理进行封装
  return new Promise<{
    /** 业务订单编号 */
    orderId: string;
  }>(async (resolve, reject) => {
    /** 统一处理错误信息 */
    const handlePayError = (rawPayErr: H5PayError) => {
      const { code } = rawPayErr;

      if (rawPayErr.code === '2002') {
        reject(new H5PayError('取消支付', code));
      } else {
        reject(rawPayErr);
      }
    };

    // 显示支付加载中提示
    if (isBoComTinyApp) { uiMgr.showLoading(); }

    try {
      await h5Pay.pay({
        payParams: {
          businessType,
          businessScene,
          payList: [{
            guid: payOrderId,
            orderId,
            amount: payMoney,
            type: '1',
          }],
          recommomdSign: true,
          failCb: (err: H5PayError) => {
            handlePayError(err);
          },
        },
        token: window.$$user.token,
        returnUrl,
        // ! 在h5浏览器等不支持promise的环境中，promise的状态不会发生流转
        isNeedCancelCall: true,
      });
      // 支付成功，隐藏 loading
      if (isBoComTinyApp) { uiMgr.hideLoading(); }

      resolve({ orderId });
    } catch (err) {
      // 支付失败或取消，隐藏 loading
      if (isBoComTinyApp) { uiMgr.hideLoading(); }

      handlePayError(err as H5PayError);
    }
  });
};

// ==================== PaymentService 类 ====================

/**
 * 支付服务类
 * 负责处理支付签约相关的所有逻辑
 */
class PaymentService {
  /** 支付签约功能所需的最低 App 版本 */
  static readonly MIN_REQUIRED_APP_VERSION = '6.93.0';

  /** H5Pay 实例（私有属性） */
  private h5Pay: H5Pay | null = null;

  /**
   * 检查 App 版本是否满足支付签约功能要求
   * @param currentVersion 当前 App 版本号
   * @returns 版本是否兼容
   */
  static checkAppVersionCompatibility(currentVersion: string): boolean {
    try {
      // 使用 compareVersions 比较版本号
      // 返回值：-1 表示 currentVersion < MIN_REQUIRED_APP_VERSION
      //         0 表示相等
      //         1 表示 currentVersion > MIN_REQUIRED_APP_VERSION
      const result = compareVersions(
        currentVersion,
        PaymentService.MIN_REQUIRED_APP_VERSION,
      );
      return result >= 0; // 大于等于最低版本即可
    } catch (error) {
      logger('checkAppVersionCompatibility-error', error);
      // 版本号格式错误时，默认返回 false（不兼容）
      return false;
    }
  }

  /**
   * 初始化 h5Pay 实例
   * @returns H5Pay 实例
   */
  private initH5Pay(): H5Pay {
    if (!this.h5Pay) {
      this.h5Pay = new H5Pay({
        env: getAppEnv(),
        systemCode: getSystemCode(),
        token: window.$$user.token,
        ubt: UBT,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        hbridge,
      });
      logger('initH5Pay', 'H5Pay 实例创建成功');
    }
    return this.h5Pay;
  }

  /**
   * 判断商品支付类型
   * @param skuData 商品数据
   * @returns 支付类型
   */
  private getPaymentType(skuData: SkuPaymentInfo): PaymentType {
    // 优先判断连续包月商品
    if (skuData.monthlyGoods) {
      if (skuData.monthlyContractChannel === 'alipay') {
        logger('getPaymentType', '识别为支付宝连续包月商品');
        return PaymentType.ALIPAY_MONTHLY;
      }
      if (skuData.monthlyContractChannel === 'douyin') {
        logger('getPaymentType', '识别为抖音连续包月商品');
        return PaymentType.DOUYIN_MONTHLY;
      }
    }

    // 再判断免密签约商品
    if (skuData.passwordFreeGoods) {
      if (skuData.passwordFreeContractChannel === 'douyin') {
        logger('getPaymentType', '识别为抖音免密签约商品');
        return PaymentType.DOUYIN_PASSWORD_FREE;
      }
    }

    // 默认为普通商品
    logger('getPaymentType', '识别为普通商品');
    return PaymentType.NORMAL;
  }

  /**
   * 查询用户签约记录
   * @param paymentType 支付类型
   * @returns 是否已签约
   */
  private async checkSignRecord(payInfo: SignPayInfoType | undefined): Promise<boolean> {
    try {
      // 根据支付类型确定签约场景（每种类型独立配置，不共用）
      const sceneList = payInfo?.sceneList || [];
      // 渠道维度（可选）
      const signChannel = payInfo?.signChannel;

      // 普通商品无需查询签约记录
      if (!sceneList.length || !signChannel) {
        logger('checkSignRecord', '普通商品无需查询签约记录');
        return false;
      }

      // switch (paymentType) {
      //   case PaymentType.ALIPAY_MONTHLY:
      //     // 支付宝连续包月
      //     sceneList = ['COUPON_CARD_MONTHLY'];
      //     signChannel = 'alipay';
      //     break;

      //   case PaymentType.DOUYIN_PASSWORD_FREE:
      //     // 抖音免密签约（2304）- 抖音先乘后付
      //     sceneList = ['RIDE'];
      //     signChannel = 'douyin';
      //     break;

      //   case PaymentType.DOUYIN_MONTHLY:
      //     // 抖音连续包月（2309）- 抖音复合签约
      //     sceneList = ['HELLO_COUPON_MONTHLY'];
      //     signChannel = 'douyinTrust';
      //     break;

      //   default:
      //     // 普通商品无需查询签约记录
      //     logger('checkSignRecord', '普通商品无需查询签约记录');
      //     return false;
      // }

      logger('checkSignRecord', `查询签约记录 - 场景：${sceneList.join(',')}${signChannel ? `, 渠道：${signChannel}` : ''}`);

      // 调用签约记录查询接口
      const response = await httpMgr.request(ApiEnums.GET_USER_SIGN_RECORD, {
        sceneList,
      });

      logger('checkSignRecord-response', response);

      // 检查签约状态
      const scene = sceneList[0];
      const sceneIsSigned = response?.data?.signRecords?.[scene] || false;

      // 如果有 signChannel，需要同时判断 Scene 维度和 Channel 维度
      if (signChannel) {
        const channelIsSigned = response?.data?.signRecords?.[signChannel] || false;
        const isSigned = sceneIsSigned && channelIsSigned;
        logger('checkSignRecord', `签约状态 - 场景[${scene}]: ${sceneIsSigned}, 渠道[${signChannel}]: ${channelIsSigned}, 最终结果: ${isSigned ? '已签约' : '未签约'}`);
        return isSigned;
      }

      // 没有 signChannel，只判断 Scene 维度
      logger('checkSignRecord', `签约状态 - 场景[${scene}]: ${sceneIsSigned ? '已签约' : '未签约'}`);
      return sceneIsSigned;
    } catch (error) {
      logger('checkSignRecord-error', error);
      // 查询失败默认返回未签约
      return false;
    }
  }

  /**
   * 新版获取下单参数配置
   * @param payInfo 支付信息
   * @returns 下单参数配置
   */
  private getNewOrderParams(payInfo: SignPayInfoType | undefined): {
    chargeType: number;
    couponMonthlyScene: string | undefined;
    couponPasswordFreeScene: string | undefined;
  } {
    const chargeType = payInfo?.chargeType && payInfo.chargeType > 0 ? payInfo.chargeType : 0;
    const couponMonthlyScene = payInfo?.couponMonthlyScene;
    const couponPasswordFreeScene = payInfo?.passwordFreeScene;

    return {
      chargeType,
      couponMonthlyScene,
      couponPasswordFreeScene,
    };
  }

  /**
   * 获取下单参数配置
   * @param paymentType 支付类型
   * @returns 下单参数配置
   */
  private getOrderParams(paymentType: PaymentType): {
    chargeType: number;
    couponMonthlyScene: string | undefined;
    couponPasswordFreeScene: string | undefined;
  } {
    switch (paymentType) {
      case PaymentType.ALIPAY_MONTHLY:
        // 支付宝连续包月
        return {
          chargeType: CHARGE_TYPE_CONFIG.ALIPAY_MONTHLY,
          couponMonthlyScene: COUPON_MONTHLY_SCENE.ALIPAY_MONTHLY_PROPERTY,
          couponPasswordFreeScene: undefined,
        };
      case PaymentType.DOUYIN_PASSWORD_FREE:
        // 抖音免密签约
        return {
          chargeType: CHARGE_TYPE_CONFIG.DOUYIN_PASSWORD_FREE,
          couponMonthlyScene: undefined,
          couponPasswordFreeScene: 'DOUYIN_PASSWORD_PROPERTY',
        };
      case PaymentType.DOUYIN_MONTHLY:
        // 抖音连续包月
        return {
          chargeType: CHARGE_TYPE_CONFIG.DOUYIN_MONTHLY,
          couponMonthlyScene: COUPON_MONTHLY_SCENE.DOUYIN_MONTHLY_PROPERTY,
          couponPasswordFreeScene: undefined,
        };
      default:
        // 默认配置
        return {
          chargeType: 0,
          couponMonthlyScene: undefined,
          couponPasswordFreeScene: undefined,
        };
    }
  }

  /**
   * 获取支付组件参数配置
   * @param paymentType 支付类型
   * @returns 支付组件参数配置
   */
  private getPayWithSignConfig(paymentType: PaymentType): {
    channel: string;
    signChannel: string;
    channelOperationInfoExt: string | undefined;
  } {
    switch (paymentType) {
      case PaymentType.ALIPAY_MONTHLY:
        // 支付宝连续包月
        return PAYMENT_CHANNEL_CONFIG.ALIPAY_MONTHLY;
      case PaymentType.DOUYIN_PASSWORD_FREE:
        // 抖音免密签约
        return PAYMENT_CHANNEL_CONFIG.DOUYIN_PASSWORD_FREE;
      case PaymentType.DOUYIN_MONTHLY:
        // 抖音连续包月
        return PAYMENT_CHANNEL_CONFIG.DOUYIN_MONTHLY;
      default:
        // 默认配置
        return {
          channel: '',
          signChannel: '',
          channelOperationInfoExt: undefined,
        };
    }
  }

  /**
   * 获取提示文案
   * @param paymentType 支付类型
   * @returns 提示文案
   */
  private getTipsText(paymentType: PaymentType): string {
    switch (paymentType) {
      case PaymentType.ALIPAY_MONTHLY:
        return '您已开通支付宝连续包月服务';
      case PaymentType.DOUYIN_PASSWORD_FREE:
        return '您已开通抖音免密支付服务';
      case PaymentType.DOUYIN_MONTHLY:
        return '您已开通抖音连续包月服务';
      default:
        return '您已开通该服务';
    }
  }

  /**
   * 支付并签约（公共方法）
   * @param skuData 商品详情数据
   * @param isFromDetail 是否来自详情页/浮层入口，影响下单后刷新策略，默认 true
   */
  async payWithSign(
    skuData: SkuDetailData & { _priceInfo: { realPrice: number; totalPrice?: number } },
    isFromDetail = false,
  ): Promise<void> {
    try {
      logger('payWithSign-start', JSON.stringify(skuData));

      // 步骤1：判断商品支付类型
      // const paymentType = specifiedPaymentType || this.getPaymentType(skuData);
      // eslint-disable-next-line no-use-before-define
      const paymentType = getPaymentTypeByPayInfo(skuData?.payInfo);
      logger('payWithSign-paymentType', paymentType);

      // 步骤2：普通商品提示并返回
      if (paymentType === PaymentType.NORMAL) {
        Toast('该商品不支持签约支付');
        return;
      }

      // 步骤3：查询签约记录
      const isSigned = await this.checkSignRecord(skuData?.payInfo);
      logger('payWithSign-isSigned', isSigned);

      // 步骤4：已签约提示并返回
      if (isSigned) {
        Toast(this.getTipsText(paymentType), {
          duration: 2000,
        });
        return;
      }

      // 获取下单参数配置
      const orderParams = this.getNewOrderParams(skuData?.payInfo);
      uiMgr.showLoading();
      // 调用下单接口
      const orderData = await reqBuyMultiGoods({
        orderType: ORDER_TYPE.NORMAL,
        // 优先使用 totalPrice（含加价购等），兜底 realPrice（列表页/订单页未传 totalPrice）
        payAmount: skuData._priceInfo.totalPrice ?? skuData._priceInfo.realPrice,
        itemList: [
          {
            skuId: skuData.skuId,
            spuId: skuData.spuId,
            num: 1,
            salePrice: skuData.salePrice,
            promotionInfos: skuData.promotionInfos,
          },
        ],
        chargeType: orderParams.chargeType,
        couponMonthlyScene: orderParams.couponMonthlyScene,
        couponPasswordFreeScene: orderParams.couponPasswordFreeScene,
        // 商品下单接口传入payInfo, 购买记录相关接口会在extra中展示payInfo信息
        extra: skuData?.payInfo ? {
          payInfo: JSON.stringify(skuData.payInfo),
        } : undefined,
      });
      logger('payWithSign-orderData', JSON.stringify(orderData));

      // 免密签约商品下单后刷新数据（需求变更V2：连续包月不支持奖励金抵扣）
      if (paymentType === PaymentType.DOUYIN_PASSWORD_FREE) {
        try {
          const allSpuIds = Object.values(
            store.state.ticket.skuDatas,
          ).flatMap((tabData: TicketTabData) => (
            tabData.list?.map((item) => String(item.spuId)) || []
          ));
          // 追加超值精选商品 spuId 并去重
          const greatValueSpuIds = (store.state.ticket.greatValueModule || [])
            .map((item) => String(item.spuId))
            .filter((id) => id && id !== 'undefined');
          const dedupedSpuIds = [...new Set([...allSpuIds, ...greatValueSpuIds])];
          handlePostOrderRefresh({
            promotionInfos: skuData.promotionInfos,
            optionalPromotions: skuData.optionalPromotions,
            isFromDetail,
            categoryId: skuData.categoryId || '',
            algorithmPriceFlag: skuData.algorithmPriceFlag,
            allSpuIds: dedupedSpuIds,
          });
        } catch {
          // 刷新失败不阻塞支付流程
        }
      }

      // 免密签约商品 0 元单判断（连续包月不支持奖励金抵扣，不需要判断）
      // 判断是否使用了奖励金：promotionInfos 中有 couponPromoType=4
      const hasNewRewardPromo = !!(skuData.promotionInfos?.some(
        (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
      ));
      if (paymentType === PaymentType.DOUYIN_PASSWORD_FREE
        && hasNewRewardPromo
        && orderData.payMoney
        && Number(orderData.payMoney) === 0
      ) {
        logger('payWithSign-免密签约商品奖励金0元单', orderData.payMoney);
        uiMgr.pushPage({
          name: RouterTicketPayResult.name,
          query: {
            isSuccess: '1',
            billNo: orderData.orderId,
            categoryId: `${skuData.categoryId}`,
          },
        });
        return;
      }

      // 获取支付组件配置
      const payConfig = this.getPayWithSignConfig(paymentType);
      logger('payWithSign-payConfig', JSON.stringify(payConfig));

      // 初始化 h5Pay
      const h5Pay = this.initH5Pay();

      // 构造支付参数（根据 h5-pay 文档）
      const params = {
        guid: orderData.payOrderId /** 必填；交易凭证号 */,
        amount: String(orderData.payMoney) /** 必填；支付金额（使用订单金额） */,
        businessType: orderData.businessType /** 必填；业务类型 */,
        orders: [
          {
            guid: orderData.payOrderId,
            amount: String(orderData.payMoney),
            businessType: orderData.businessType,
          },
        ] /** 该字段仅嵌入 app 端需要 */,
        channel: payConfig.channel /** 支付渠道 */,
        signChannel: payConfig.signChannel /** 免密签约渠道（废弃，优先使用 channel） */,
        channelOperationInfoExt: payConfig.channelOperationInfoExt /** 渠道扩展字段 */,
        creditModel: 'normal' /** 必填；请求授信模式：normal 普通模式 */,
        actionOrigin: 'AppPlatformH5' /** 必填；动作行为来源 */,
        extension: paymentType === PaymentType.DOUYIN_MONTHLY
          ? {
            signMustPay: true /** 抖音支付并签约需要强制签约 */,
            attachSceneType: 'RIDE' /** 附属签约场景（channel: 2309 时必填） */,
            contractDisplayAccount: window.$$user.token?.mobile || `骑行侠${+new Date()}` /** 签约展示账号 */,
          }
          : undefined,
      };
      logger('payWithSign-params', JSON.stringify(params));

      // 调用支付组件
      const result = await h5Pay.payWithSign(params);
      logger('payWithSign-result', JSON.stringify(result));

      // 支付成功跳转结果页
      uiMgr.pushPage(
        `/TicketPayResult?isSuccess=1&categoryId=${skuData.categoryId}`,
      );
    } catch (error) {
      logger('payWithSign-error', error);
      Toast.fail((error as Error).message || '支付失败，请稍后重试');
    } finally {
      uiMgr.hideLoading();
    }
  }
}

// 创建单例实例
const paymentService = new PaymentService();

/**
 * 导出支付签约方法（保持原有接口兼容）
 * @param skuData 商品详情数据
 * @param isFromDetail 是否来自详情页/浮层入口，影响下单后刷新策略，默认 true
 */
export const payWithSignNew = async (
  skuData: SkuDetailData & { _priceInfo: { realPrice: number; totalPrice?: number } },
  isFromDetail = false,
): Promise<void> => paymentService.payWithSign(skuData, isFromDetail);

/**
 * 导出版本检查方法
 * @param currentVersion 当前 App 版本号
 * @returns 版本是否兼容
 */
export const checkPayWithSignVersionCompatibility = (
  currentVersion: string,
): boolean => PaymentService.checkAppVersionCompatibility(currentVersion);

/**
 * 导出最低版本要求常量
 */
export const PAY_WITH_SIGN_MIN_VERSION = PaymentService.MIN_REQUIRED_APP_VERSION;

/**
 * 根据支付信息判断商品支付类型
 * @param skuData 商品数据
 * @returns 支付类型
 */
export const getPaymentTypeByPayInfo = (payInfo: SignPayInfoType | undefined): PaymentType => {
  const { couponMonthlyScene, passwordFreeScene } = payInfo || {};

  // 1. 优先判断连续包月商品
  if (couponMonthlyScene) {
    if (couponMonthlyScene === COUPON_MONTHLY_SCENE.ALIPAY_MONTHLY_PROPERTY) {
      logger('getPaymentTypeByPayInfo', '识别为支付宝连续包月商品');
      return PaymentType.ALIPAY_MONTHLY;
    }
    if (couponMonthlyScene === COUPON_MONTHLY_SCENE.DOUYIN_MONTHLY_PROPERTY) {
      logger('getPaymentTypeByPayInfo', '识别为抖音连续包月商品');
      return PaymentType.DOUYIN_MONTHLY;
    }
  }

  // 2. 再判断免密签约商品
  if (passwordFreeScene) {
    if (passwordFreeScene === PASSWORD_SCENE.DOUYIN_PASSWORD_PROPERTY) {
      logger('getPaymentTypeByPayInfo', '识别为抖音免密签约商品');
      return PaymentType.DOUYIN_PASSWORD_FREE;
    }
  }

  // 3. 默认为普通商品
  logger('getPaymentTypeByPayInfo', '识别为普通商品');
  return PaymentType.NORMAL;
};
